import React, { useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { TableCard } from "@/components/ui/table-card";
import { FilterCard, FilterSearch } from "@/components/ui/filter-card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import {
  getConnectionIcon,
  statusVariant,
  CONNECTION_TYPES,
  STATUS_OPTIONS,
  PAPER_SIZE_PRESETS,
} from "./printerConstants";
import {
  Printer,
  PlusCircle,
  Plus,
  Edit,
  Trash2,
  MoreVertical,
  Star,
  Ruler,
  Check,
  Layers,
  Sparkles,
  Info,
  AlertTriangle,
  FileText,
  Eye,
} from "lucide-react";
import { cn } from "@/lib/utils";

export default function PrintersList({
  printers = [],
  allPaperSizes = [],
  paperSizeAssignments = {},
  isLoading = false,
  onAddPrinter,
  onEditPrinter,
  onDeletePrinter,
  onSetDefaultPrinter,
  onAddPaperSize,
  onEditPaperSize,
  onDeletePaperSize,
  onSetDefaultPaperSize,
  onOpenAssignments,
  onQuickAddPresetPaper,
  onPreviewTemplate,
}) {
  const permissions = useSelector((state) => state.userpermission_data) || [];
  const hasPermission = (p) => {
    if (!permissions) return false;
    if (Array.isArray(p)) return p.some((perm) => permissions.includes(perm));
    return permissions.includes(p);
  };

  const canEdit = hasPermission("printer update");
  const canDelete = hasPermission("printer delete");
  const canCreate = hasPermission("printer create");

  const [searchText, setSearchText] = useState("");
  const [connectionFilter, setConnectionFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  // Filtered printers
  const filteredPrinters = useMemo(() => {
    return printers.filter((p) => {
      const matchesSearch =
        searchText === "" ||
        p.name?.toLowerCase().includes(searchText.toLowerCase()) ||
        p.address?.toLowerCase().includes(searchText.toLowerCase()) ||
        p.connection_type?.toLowerCase().includes(searchText.toLowerCase());

      const matchesConn =
        connectionFilter === "all" || p.connection_type === connectionFilter;

      const matchesStatus =
        statusFilter === "all" || p.status === statusFilter;

      return matchesSearch && matchesConn && matchesStatus;
    });
  }, [printers, searchText, connectionFilter, statusFilter]);

  // Group paper sizes by printer id
  const paperSizesByPrinter = useMemo(() => {
    const map = {};
    allPaperSizes.forEach((ps) => {
      if (!map[ps.printer_id]) {
        map[ps.printer_id] = [];
      }
      map[ps.printer_id].push(ps);
    });
    return map;
  }, [allPaperSizes]);

  return (
    <div className="space-y-4">
      {/* Search & Filters */}
      <FilterCard>
        <div className="flex flex-col sm:flex-row gap-3 items-center justify-between w-full">
          <div className="w-full sm:w-72">
            <FilterSearch
              placeholder="Search printers by name, IP, or connection…"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </div>

          <div className="flex flex-wrap items-center gap-2.5 w-full sm:w-auto">
            {/* Connection Filter */}
            <div className="w-full sm:w-44">
              <Select
                value={connectionFilter}
                onValueChange={setConnectionFilter}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Connections" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Connections</SelectItem>
                  {CONNECTION_TYPES.map((c) => (
                    <SelectItem key={c.value} value={c.value}>
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Status Filter */}
            <div className="w-full sm:w-36">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  {STATUS_OPTIONS.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </FilterCard>

      {/* Printers Cards List */}
      {isLoading ? (
        <TableCard>
          <div className="flex flex-col items-center justify-center py-20 text-muted-foreground gap-2.5">
            <Spinner className="h-6 w-6" />
            <span className="text-xs">Loading configured printers…</span>
          </div>
        </TableCard>
      ) : filteredPrinters.length === 0 ? (
        <TableCard>
          <div className="flex flex-col items-center justify-center py-16 text-center px-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary mb-3">
              <Printer className="h-6 w-6" />
            </div>
            <h3 className="text-sm font-semibold text-foreground">
              No printers found
            </h3>
            <p className="text-xs text-muted-foreground max-w-sm mt-1">
              {searchText || connectionFilter !== "all"
                ? "No printers match your search filters. Try resetting the filter."
                : "Register your first receipt, laser, or label printer to enable automated printing."}
            </p>
            {canCreate && !searchText && (
              <Button onClick={onAddPrinter} className="mt-4 gap-1.5" size="sm">
                <PlusCircle className="h-4 w-4" />
                Register Printer
              </Button>
            )}
          </div>
        </TableCard>
      ) : (
        <div className="space-y-4">
          {filteredPrinters.map((p) => {
            const paperSizes = paperSizesByPrinter[p.id] || [];
            const hasPaper = paperSizes.length > 0;

            return (
              <div
                key={p.id}
                className={cn(
                  "rounded-xl border bg-card text-card-foreground shadow-2xs overflow-hidden transition-all duration-150",
                  p.is_default
                    ? "border-primary/40 ring-1 ring-primary/20"
                    : "border-border/80 hover:border-border"
                )}
              >
                {/* Printer Header */}
                <div className="p-4 sm:p-5 border-b border-border bg-muted/10 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex items-start gap-3.5">
                    <div
                      className={cn(
                        "flex h-11 w-11 items-center justify-center rounded-xl shrink-0",
                        p.is_default
                          ? "bg-primary text-primary-foreground shadow-xs"
                          : "bg-primary/10 text-primary"
                      )}
                    >
                      <Printer className="h-5 w-5" />
                    </div>

                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-sm font-semibold text-foreground">
                          {p.name}
                        </h3>
                        {p.is_default && (
                          <Badge
                            variant="outline"
                            className="text-primary border-primary gap-1 text-[10px] font-medium py-0 h-4.5 bg-primary/5"
                          >
                            Store Default
                          </Badge>
                        )}
                        <Badge
                          variant={statusVariant(p.status)}
                          className="capitalize text-[10px] py-0 h-4.5 font-normal"
                        >
                          {p.status}
                        </Badge>
                      </div>

                      <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1 flex-wrap">
                        <span className="flex items-center gap-1.5 capitalize font-medium">
                          {React.createElement(getConnectionIcon(p.connection_type), {
                            className: "h-4 w-4",
                          })}
                          {CONNECTION_TYPES.find((c) => c.value === p.connection_type)
                            ?.label || p.connection_type}
                        </span>
                        {p.address && (
                          <>
                            <span>•</span>
                            <span className="font-mono text-[11px]">
                              {p.address}
                            </span>
                          </>
                        )}
                        <span>•</span>
                        <span>
                          {paperSizes.length}{" "}
                          {paperSizes.length === 1 ? "paper tray" : "paper trays"}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Printer Actions */}
                  <div className="flex items-center gap-2 self-end sm:self-center shrink-0">
                    {(canCreate || canEdit) && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onAddPaperSize(p)}
                        className="h-8 gap-1.5 text-xs font-medium cursor-pointer"
                      >
                        <PlusCircle className="h-3.5 w-3.5 text-primary" />
                        Add Paper Tray
                      </Button>
                    )}

                    {(canEdit || canDelete) && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            className="h-8 w-8 p-0 cursor-pointer"
                          >
                            <span className="sr-only">Open menu</span>
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {canEdit && !p.is_default && (
                            <DropdownMenuItem
                              onClick={() => onSetDefaultPrinter(p.id)}
                            >
                              <Star className="mr-2 h-4 w-4 text-amber-500" />
                              Set as Store Default
                            </DropdownMenuItem>
                          )}
                          {canEdit && (
                            <DropdownMenuItem onClick={() => onEditPrinter(p)}>
                              <Edit className="mr-2 h-4 w-4" />
                              Edit Printer
                            </DropdownMenuItem>
                          )}
                          {canEdit && canDelete && <DropdownMenuSeparator />}
                          {canDelete && (
                            <DropdownMenuItem
                              className="text-destructive focus:text-destructive"
                              onClick={() => onDeletePrinter(p)}
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Delete Printer
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                </div>

                {/* Paper Sizes & Document Mapping Section */}
                <div className="p-4 sm:p-5">
                  {!hasPaper ? (
                    <div className="rounded-xl border border-dashed border-border p-4 bg-muted/15 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-500/10 text-amber-600 dark:text-amber-400 shrink-0">
                          <AlertTriangle className="h-4 w-4" />
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-foreground">
                            No Paper Size Configured
                          </p>
                          <p className="text-[11px] text-muted-foreground mt-0.5">
                            This printer needs at least one paper size to receive print jobs.
                          </p>
                        </div>
                      </div>

                      {(canCreate || canEdit) && (
                        <div className="flex items-center gap-2 flex-wrap">
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() =>
                              onQuickAddPresetPaper(p, "80mm_thermal", "sales_receipt")
                            }
                            className="h-7 text-xs gap-1 font-medium cursor-pointer"
                          >
                            <Sparkles className="h-3 w-3 text-primary" />
                            + 80mm Receipt
                          </Button>
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() =>
                              onQuickAddPresetPaper(p, "a4", "sales_invoice")
                            }
                            className="h-7 text-xs gap-1 font-medium cursor-pointer"
                          >
                            <Sparkles className="h-3 w-3 text-primary" />
                            + A4 Document
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => onAddPaperSize(p)}
                            className="h-7 text-xs cursor-pointer"
                          >
                            Custom…
                          </Button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-2.5">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                          <Ruler className="h-3.5 w-3.5" />
                          Configured Paper Trays & Document Assignments
                        </span>
                        <span className="text-[11px] text-muted-foreground">
                          {paperSizes.length}{" "}
                          {paperSizes.length === 1 ? "tray configured" : "trays configured"}
                        </span>
                      </div>

                      <div className="grid grid-cols-1 gap-2.5">
                        {paperSizes.map((ps) => {
                          const isContinuous =
                            Number(ps.height) === 0 ||
                            ps.size === "thermal_80" ||
                            ps.size === "thermal_58";

                          const dimString =
                            ps.width != null && ps.height != null
                              ? isContinuous
                                ? `${ps.width} ${ps.unit || "inch"} • Continuous Roll`
                                : `${ps.width} × ${ps.height} ${ps.unit || "mm"}`
                              : ps.size.toUpperCase();

                          const assignments = paperSizeAssignments[ps.id] || [];

                          return (
                            <div
                              key={ps.id}
                              className={cn(
                                "p-3.5 rounded-xl border transition-all duration-150 flex flex-col gap-2.5",
                                ps.is_default
                                  ? "border-border/90 bg-muted/15"
                                  : "border-border/60 bg-card hover:border-border"
                              )}
                            >
                              {/* Top Row: Paper info & Actions */}
                              <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                                {/* Left: Paper info */}
                                <div className="flex items-center gap-3 min-w-0">
                                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary shrink-0">
                                    <Ruler className="h-4 w-4" />
                                  </div>
                                  <div className="min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span className="text-xs font-semibold text-foreground">
                                        {ps.label}
                                      </span>
                                      <Badge
                                        variant="secondary"
                                        className="text-[10px] py-0 px-1.5 h-4 font-mono uppercase"
                                      >
                                        {ps.size}
                                      </Badge>
                                      {ps.is_default && (
                                        <Badge
                                          variant="outline"
                                          className="text-primary border-primary gap-1 text-[10px] font-medium py-0 h-4"
                                        >
                                          <Check className="h-2.5 w-2.5" />
                                          Default Paper
                                        </Badge>
                                      )}
                                      {assignments.length > 0 && (
                                        <Badge
                                          variant="outline"
                                          className="text-[10px] py-0 px-1.5 h-4 bg-primary/5 text-primary border-primary/30 font-medium"
                                        >
                                          {assignments.length}{" "}
                                          {assignments.length === 1 ? "doc routed" : "docs routed"}
                                        </Badge>
                                      )}
                                    </div>
                                    <p className="text-[11px] text-muted-foreground mt-0.5">
                                      {dimString} &bull;{" "}
                                      <span className="capitalize">
                                        {ps.orientation || "portrait"}
                                      </span>
                                    </p>
                                  </div>
                                </div>

                                {/* Right: Actions */}
                                <div className="flex items-center gap-2 shrink-0 self-end md:self-auto">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => onOpenAssignments(ps, p)}
                                    className="h-7 px-2.5 text-xs gap-1.5 font-medium hover:bg-primary/10 hover:text-primary hover:border-primary/40 cursor-pointer"
                                    title={
                                      canEdit
                                        ? "Configure which document templates print on this paper size"
                                        : "View assigned document templates"
                                    }
                                  >
                                    <Layers className="h-3.5 w-3.5 text-primary" />
                                    {canEdit ? "Configure Documents" : "View Documents"}
                                  </Button>

                                  {canEdit && (
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-7 w-7 text-muted-foreground hover:text-foreground cursor-pointer"
                                      onClick={() => onEditPaperSize(ps, p)}
                                      title="Edit paper dimensions"
                                    >
                                      <Edit className="h-3.5 w-3.5" />
                                    </Button>
                                  )}

                                  {canEdit && !ps.is_default && (
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-7 w-7 text-muted-foreground hover:text-amber-500 cursor-pointer"
                                      onClick={() => onSetDefaultPaperSize(ps)}
                                      title="Set as default paper size for this printer"
                                    >
                                      <Star className="h-3.5 w-3.5" />
                                    </Button>
                                  )}

                                  {canDelete && (
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-7 w-7 text-muted-foreground hover:text-destructive hover:bg-destructive/10 cursor-pointer"
                                      onClick={() => onDeletePaperSize(ps, p)}
                                      title="Delete paper size"
                                    >
                                      <Trash2 className="h-3.5 w-3.5" />
                                    </Button>
                                  )}
                                </div>
                              </div>

                              {/* Assigned Templates List (GET /v1/paper-sizes/{id}/templates) */}
                              <div className="pt-2 border-t border-border/40">
                                {assignments.length > 0 ? (
                                  <div className="space-y-1.5">
                                    <div className="flex items-center justify-between text-xs">
                                      <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                                        <FileText className="h-3 w-3 text-primary" />
                                        Assigned Document Templates ({assignments.length})
                                      </span>
                                      {canEdit && (
                                        <button
                                          type="button"
                                          onClick={() => onOpenAssignments(ps, p)}
                                          className="text-[11px] text-primary hover:underline font-medium cursor-pointer"
                                        >
                                          Configure &rarr;
                                        </button>
                                      )}
                                    </div>

                                    <div className="flex flex-wrap gap-1.5">
                                      {assignments.map((item) => (
                                        <div
                                          key={item.id || item.template_type}
                                          className={cn(
                                            "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs transition-colors shadow-2xs",
                                            item.is_active !== false
                                              ? "bg-card border-border/80 text-foreground"
                                              : "bg-muted/30 border-border/40 text-muted-foreground opacity-75"
                                          )}
                                        >
                                          <span
                                            className={cn(
                                              "h-1.5 w-1.5 rounded-full shrink-0",
                                              item.is_active !== false
                                                ? "bg-emerald-500"
                                                : "bg-muted-foreground/40"
                                            )}
                                            title={
                                              item.is_active !== false
                                                ? "Active template assignment"
                                                : "Inactive template assignment"
                                            }
                                          />
                                          <span className="font-semibold text-foreground">
                                            {item.template_type_label || item.template_type}
                                          </span>
                                          {item.template_content?.name && (
                                            <>
                                              <span className="text-muted-foreground/50">:</span>
                                              <span
                                                className="text-muted-foreground font-normal truncate max-w-[170px]"
                                                title={item.template_content.name}
                                              >
                                                {item.template_content.name}
                                              </span>
                                            </>
                                          )}
                                          {onPreviewTemplate && (
                                            <button
                                              type="button"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                onPreviewTemplate(
                                                  item.template_content || {
                                                    id: item.template_content_id,
                                                    name:
                                                      item.template_type_label ||
                                                      item.template_type,
                                                    type: item.template_type,
                                                  }
                                                );
                                              }}
                                              className="ml-1 text-muted-foreground hover:text-foreground cursor-pointer p-0.5 rounded hover:bg-muted"
                                              title="Preview assigned template"
                                            >
                                              <Eye className="h-3 w-3" />
                                            </button>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                ) : (
                                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                                    <span className="text-[11px] italic">
                                      No document templates assigned to this paper tray yet.
                                    </span>
                                    {canEdit && (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => onOpenAssignments(ps, p)}
                                        className="h-6 px-2 text-[11px] text-primary hover:text-primary gap-1 font-medium cursor-pointer"
                                      >
                                        <Plus className="h-3 w-3" />
                                        Assign Documents
                                      </Button>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
